import React, { useState } from "react";

import { ErrorMessage, Heading1, PrimaryButton } from "widgets";
import SingleSelect from "widgets/Dropdown/SingleSelect";
import {
  UPLOAD_SCRIPT_TYPE,
  FILE_UPLOAD_STATUS,
  PAGE_SIZE_OPTIONS
} from "constants/constants";
import { Input } from "@mui/material";
import {
  useFetchHistory,
  useFetchProcessedFile,
  useUploadCsv,
} from "store/backend/apiHooks";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import Loader from "shared/Loader/Loader";

import styles from "./Upload.module.scss";

const handleFileChange = (event, setFile, setFileError) => {
  const selectedFile = event.target.files[0];
  if (!is_valid_file(selectedFile)) {
    setFileError("Invalid file format. Please upload a CSV file.");
  } else {
    setFileError("");
    setFile(selectedFile);
  }
};

const handleOnClickDownloadProcessedFile = (
  fileId,
  fetchProcessedFile,
  openSnackbar,
) => {
  if (fileId) {
    fetchProcessedFile(fileId)
      .then((response) => {
        const link = document.createElement("a");
        link.href = response.signedUrl;
        link.click();
        openSnackbar("Processed File downloaded successfully!", "success");
      })
      .catch((error) => {
        console.error("Error fetching Processed File", error);
        openSnackbar(error.message || "Error fetching Processed File", "error");
      });
  } else {
    console.error("No File Id provided");
  }
};

const handleSubmit = (
  file,
  selectedScriptId,
  openSnackbar,
  processUploadCsv,
  uploaderUserId,
) => {
  processUploadCsv(
    { file, selectedScriptId, uploaderUserId },
    {
      onSuccess: (data) => {
        if (!data) {
          throw new Error("Received data is undefined");
        }
        window.location.reload();
        if (data.uploaded_file_id) {
          openSnackbar(`File Uploaded Successfully!`, "success");
        }
      },
      onError: (err) => {
        openSnackbar(err.message || "Download failed", "error");
      },
    },
  );
};

const is_valid_file = (file) => {
  if (!file) {
    return false;
  }
  const fileExtension = file.name.split(".").pop().toLowerCase();
  return fileExtension === "csv";
};

const Upload = () => {
  const { openSnackbar } = useSnackbarContext();
  const [file, setFile] = useState(null);
  const [selectedScriptId, setSelectedScriptId] = useState("");
  const [fileError, setFileError] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const uploaderUserId = localStorage.getItem("cashierCRNId");

  const offset = (page - 1) * pageSize;

  const { mutate: processUploadCsv, isPending: isUploadPending } =
    useUploadCsv();
  const {
    isPending: isHistoryLoading,
    data: history,
    error: historyError,
  } = useFetchHistory(uploaderUserId, pageSize, offset);

  const { fetchProcessedFile, isLoading: isFetchProcessedFileLoading } =
    useFetchProcessedFile();

  const historyList = Array.isArray(history) ? history : [];
  const sortedHistory = [...historyList].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );
  const hasMore = sortedHistory.length === pageSize;
  const hasPrevious = page > 1;

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.uploadContainer}>
        <div className={styles.action}>
          <Heading1 heading="Choose an action" />
          <SingleSelect
            tag="Select action"
            menuItems={UPLOAD_SCRIPT_TYPE}
            value={selectedScriptId}
            handleChange={(event) =>
              setSelectedScriptId(event.target.value)
            }
          />
        </div>

        {selectedScriptId && (
          <div className={styles.action}>
            <Heading1 heading="Upload File" />
            <div className={styles.input}>
              <Input
                id="file-upload"
                type="file"
                display="none"
                className={styles.inputField}
                onChange={(event) =>
                  handleFileChange(event, setFile, setFileError)
                }
              />
              {file && <p>Selected file: {file.name}</p>}
            </div>
          </div>
        )}
        <div className={styles.button}>
          <PrimaryButton
            onClick={() =>
              handleSubmit(
                file,
                selectedScriptId,
                openSnackbar,
                processUploadCsv,
                uploaderUserId,
              )
            }
            disabled={
              !file || fileError || !selectedScriptId || isUploadPending
            }
          >
            {isUploadPending ? "Uploading..." : "Upload"}
          </PrimaryButton>
        </div>
        {fileError && <ErrorMessage>{fileError}</ErrorMessage>}
      </div>
      <div className={styles.historyContainer}>
        <div className={styles.historyHeader}>
          <h2>Upload History</h2>
          <div className={styles.paginationControls}>
            <div className={styles.pageSizeWrap}>
              <SingleSelect
                tag="Records per page"
                menuItems={PAGE_SIZE_OPTIONS}
                value={pageSize}
                handleChange={handlePageSizeChange}
              />
            </div>
            <div className={styles.pageNav}>
              <PrimaryButton
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={!hasPrevious || isHistoryLoading}
              >
                Previous
              </PrimaryButton>
              <span className={styles.pageInfo}>Page {page}</span>
              <PrimaryButton
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore || isHistoryLoading}
              >
                Next
              </PrimaryButton>
            </div>
          </div>
        </div>
        {historyError && (
          <p>Error fetching History: {historyError.message}</p>
        )}
        {!historyError && isHistoryLoading && (
          <div className={styles.tableLoader}>
            <Loader />
          </div>
        )}
        {!historyError && !isHistoryLoading &&
          (sortedHistory.length > 0 ? (
            <table className={styles.historyTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Script</th>
                  <th>Created At</th>
                  <th>Total Rows</th>
                  <th>Error Rows</th>
                  <th>Status</th>
                  <th>Download Processed File</th>
                </tr>
              </thead>
              <tbody>
                {sortedHistory.map((item) => (
                  <tr key={item.file_id}>
                    <td>{item.name}</td>
                    <td>{item.script.name}</td>
                    <td>
                      {(() => {
                        const date = new Date(item.created_at);
                        return `${date.getUTCDate()}-${
                          date.getUTCMonth() + 1
                        }-${date.getUTCFullYear()} ${date.getUTCHours()}:${date.getUTCMinutes()}`;
                      })()}
                    </td>
                    <td>{item.result?.total_rows || "-"}</td>
                    <td>{item.result?.error_rows || "-"}</td>
                    <td>{item.status.name}</td>
                    <td>
                      {item.status.id === FILE_UPLOAD_STATUS.PROCESSED ? (
                        <PrimaryButton
                          onClick={() =>
                            handleOnClickDownloadProcessedFile(
                              item.file_id,
                              fetchProcessedFile,
                              openSnackbar,
                            )
                          }
                          disabled={isFetchProcessedFileLoading}
                        >
                          Download
                        </PrimaryButton>
                      ) : (
                        <PrimaryButton disabled>Not Available Yet</PrimaryButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>Nothing to show yet...</p>
          ))}
      </div>
    </div>
  );
};

export default Upload;
